import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '../layouts/MainLayout';
import PromoCodeModal from '../components/PromoCodeModal';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { GET_SUBSCRIPTION_PACKAGES } from '@shared/graphql/queries/subscriptionPackages';
import { CREATE_PAYMENT_INTENT } from '@shared/graphql/mutations/payment';
import { useAffiliateTracking } from '../hooks/useAffiliateTracking';
import { usePromoTracking } from '../hooks/usePromoTracking';
import analyticsService from '../services/analytics';
import googleAnalytics from '../services/googleAnalytics';
import { resolveDiscountCode } from '@shared/ui/lib/discount-code';
import { saveCheckoutIntent } from '@shared/ui/checkout/checkout-intent';
import { BillingToggle, PlanCard, isPopularPlan, type BillingCycle } from '../components/subscription/PlanCard';
import { usePageDocumentTitle } from '../hooks/useDocumentTitle';

interface SubscriptionPackage {
  id: string;
  packageCode: string;
  name: string;
  description: string;
  priceWeekly: number;
  priceMonthly: number;
  priceOneTime?: number;
  billingModel: string;
  accessDurationDays?: number;
  allowMultipleSubscriptions: boolean;
  displayOrder: number;
  isActive: boolean;
  hasTrackRestrictions?: boolean;
  tagRules: Array<{
    id: string;
    tag: {
      id: string;
      name: string;
    };
  }>;
}

const PricingPage: React.FC = () => {
  usePageDocumentTitle('Pricing and Plans');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const { isSubscribed, activeSubscriptions } = useSubscriptionAccess();
  const hasTrackedPageView = useRef(false);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [promoInitialCode, setPromoInitialCode] = useState<string | undefined>();

  // Filter out default packages to show only paid subscriptions
  const paidSubscriptions = activeSubscriptions.filter(sub =>
    sub.package.packageCode !== 'DEFAULT' &&
    sub.package.name !== 'Default Package' &&
    sub.isActive
  );
  const [billingCycle, setBillingCycle] = useState<'WEEKLY' | 'MONTHLY'>('WEEKLY');
  const [loading, setLoading] = useState<string | null>(null);

  const { data, loading: packagesLoading, error } = useQuery(GET_SUBSCRIPTION_PACKAGES, {
    errorPolicy: 'all'
  });

  const [createPaymentIntent] = useMutation(CREATE_PAYMENT_INTENT);
  const { getAffiliateForRegistration } = useAffiliateTracking();
  const { promotion, getUserDiscountCode } = usePromoTracking();

  const packages = (data?.getAvailablePackages || []) as SubscriptionPackage[];
  
  // Sort packages by display order and ensure tagRules is always safe
  const sortedPackages = packages
    .filter(pkg => pkg?.isActive)
    .map(pkg => ({
      ...pkg,
      tagRules: pkg.tagRules || []
    }))
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  // Auto-open promo modal if ?code= is in URL (from email campaign links)
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      setPromoInitialCode(code);
      setPromoModalOpen(true);
    }
  }, [searchParams]);

  // Analytics tracking for page view
  useEffect(() => {
    if (!hasTrackedPageView.current && !packagesLoading && !error) {
      try {
        analyticsService.trackPageView('pricing', undefined, new Date().toISOString().split('T')[0], {
          isAuthenticated,
          packagesCount: sortedPackages.length,
          hasActiveSubscriptions: paidSubscriptions.length > 0,
          activeSubscriptionsCount: paidSubscriptions.length,
          billingCycle: billingCycle,
          userType: isAuthenticated ? 'authenticated' : 'anonymous'
        });
        
        hasTrackedPageView.current = true;
        console.log('📊 Analytics: PricingPage view tracked');
      } catch (error) {
        console.error('Analytics error:', error);
        // Don't let analytics errors break the app
      }
    }
  }, [packagesLoading, error, isAuthenticated, sortedPackages.length, paidSubscriptions.length, billingCycle]);

  const handleSubscribe = async (pkg: SubscriptionPackage) => {
    // Track subscription attempt
    try {
      analyticsService.trackEvent('subscription_attempt', {
        pageType: 'pricing',
        selectedDate: new Date().toISOString().split('T')[0],
        metadata: {
          packageId: pkg.id,
          packageName: pkg.name,
          packageCode: pkg.packageCode,
          billingModel: pkg.billingModel,
          billingCycle: billingCycle,
          price: getPriceForCycle(pkg),
          isAuthenticated,
          userType: isAuthenticated ? 'authenticated' : 'anonymous'
        }
      });
    } catch (analyticsError) {
      console.error('Analytics error:', analyticsError);
    }

    if (!isAuthenticated) {
      // Store intended package and redirect to register
      sessionStorage.setItem('intendedPackage', JSON.stringify(pkg));
      // after sign-up, carry on to checkout for this package (CheckoutIntentResumer)
      saveCheckoutIntent(pkg.packageCode, billingCycle === 'WEEKLY' ? '?cycle=weekly' : '');
      navigate('/register');
      return;
    }

    setLoading(pkg.id);
    
    try {
      // For one-time packages that require track selection, redirect to main page
      if (pkg.billingModel === 'ONE_TIME' && pkg.hasTrackRestrictions) {
        // Don't handle track-restricted one-time packages here - users need to select a track first
        return;
      }

      // For subscription packages and unrestricted one-time packages, create Stripe checkout session and redirect
      const affiliateCode = getAffiliateForRegistration();
      
      // Get user's assigned discount code (for logged-in users)
      const userDiscountCode = user ? await getUserDiscountCode() : null;
      
      // Get discount code based on user authentication status and available promotions
      const discountCode = resolveDiscountCode({
        fromUrl: searchParams.get('discount'),
        userAssigned: user ? userDiscountCode : null,
        promotion: promotion?.discountCode,
      });
      
      console.log('🎯 PricingPage: User logged in:', !!user);
      console.log('🎯 PricingPage: User discount code:', userDiscountCode);
      console.log('🎯 PricingPage: Campaign promotion code:', promotion?.discountCode);
      console.log('🎯 PricingPage: Final discount code:', discountCode);
      
      const { data } = await createPaymentIntent({
        variables: {
          input: {
            packageIds: [parseInt(pkg.id)],
            billingCycle: billingCycle,
            paymentType: 'SUBSCRIPTION',
            affiliateCode: affiliateCode,
            discountCode: discountCode,
          }
        }
      });

      if (data?.createPaymentIntent?.checkoutUrl) {
        // Track successful checkout session creation
        try {
          analyticsService.trackEvent('checkout_session_created', {
            pageType: 'pricing',
            selectedDate: new Date().toISOString().split('T')[0],
            metadata: {
              packageId: pkg.id,
              packageName: pkg.name,
              billingCycle: billingCycle,
              checkoutUrl: data.createPaymentIntent.checkoutUrl
            }
          });
        } catch (analyticsError) {
          console.error('Analytics error:', analyticsError);
        }

        googleAnalytics.trackBeginCheckout({
          value: getPriceForCycle(pkg),
          currency: 'AUD',
          items: [{
            item_id: pkg.packageCode,
            item_name: pkg.name,
            price: getPriceForCycle(pkg),
            quantity: 1
          }]
        });

        // Redirect to Stripe Checkout
        window.location.href = data.createPaymentIntent.checkoutUrl;
      } else {
        throw new Error('Failed to create checkout session - no checkout URL received');
      }
    } catch (error) {
      console.error('Error subscribing to package:', error);
      
      // Track subscription failure
      try {
        analyticsService.trackEvent('subscription_failure', {
          pageType: 'pricing',
          selectedDate: new Date().toISOString().split('T')[0],
          metadata: {
            packageId: pkg.id,
            packageName: pkg.name,
            error: error instanceof Error ? error.message : 'Unknown error',
            billingCycle: billingCycle
          }
        });
      } catch (analyticsError) {
        console.error('Analytics error:', analyticsError);
      }
      
      alert('Failed to initialize membership. Please try again.');
    } finally {
      setLoading(null);
    }
  };


  const isPackageActive = (pkg: SubscriptionPackage) => {
    return activeSubscriptions.some(sub => sub.package.id === pkg.id && sub.isActive);
  };

  const getButtonText = (pkg: SubscriptionPackage) => {
    if (!isAuthenticated) return 'Get Started';
    if (isPackageActive(pkg)) return 'Current Plan';
    
    // For one-time packages that require track selection, show instruction instead of button
    if (pkg.billingModel === 'ONE_TIME' && pkg.hasTrackRestrictions) {
      return 'Select Track First';
    }
    return 'Join Now';
  };

  const getButtonClasses = (pkg: SubscriptionPackage) => {
    const baseClasses = "w-full px-6 py-3 rounded-lg font-medium transition-colors";
    
    if (isPackageActive(pkg)) {
      return `${baseClasses} bg-gray-200 text-gray-600 cursor-not-allowed`;
    }
    
    // Highlight popular packages
    if (pkg.packageCode.toLowerCase().includes('premium') || pkg.packageCode.toLowerCase().includes('everything')) {
      return `${baseClasses} bg-racing-green text-white hover:bg-racing-green-dark`;
    }
    
    return `${baseClasses} bg-deep-navy text-white hover:bg-deep-navy-dark`;
  };

  const getPriceForCycle = (pkg: SubscriptionPackage) => {
    if (pkg.billingModel === 'ONE_TIME' && pkg.priceOneTime) {
      return pkg.priceOneTime;
    }
    return billingCycle === 'WEEKLY' ? pkg.priceWeekly : pkg.priceMonthly;
  };

  const getCycleSavings = () => {
    if (billingCycle === 'MONTHLY' && sortedPackages.length > 0) {
      // Calculate average savings for monthly vs weekly
      const avgWeeklyPrice = sortedPackages.reduce((sum, pkg) => sum + (pkg?.priceWeekly || 0), 0) / sortedPackages.length;
      const avgMonthlyPrice = sortedPackages.reduce((sum, pkg) => sum + (pkg?.priceMonthly || 0), 0) / sortedPackages.length;
      const weeklyMonthlyEquivalent = avgWeeklyPrice * 4.33; // Approximate weeks per month
      const savings = weeklyMonthlyEquivalent > 0 ? ((weeklyMonthlyEquivalent - avgMonthlyPrice) / weeklyMonthlyEquivalent) * 100 : 0;
      return Math.round(savings);
    }
    return 0;
  };

  if (packagesLoading) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-racing-green"></div>
            <p className="mt-4 text-gray-600">Loading pricing plans...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Error loading pricing plans</p>
            <p className="text-sm text-gray-600">{error.message}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Final safety check for packages data
  if (!sortedPackages || sortedPackages.length === 0) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">No membership packages available at the moment.</p>
            <p className="text-sm text-gray-500">Please check back later or contact support.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-deep-navy mb-4">
            Choose Your Racing Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get access to premium racing insights, analysis, and tips to boost your betting success
          </p>
        </div>

        {/* Redeemed promo code: confirms the discount arrived, since it is
            otherwise invisible until the Stripe checkout page. */}
        {searchParams.get('discount') && (
          <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-xl text-center">
            <p className="text-green-800">
              Discount code <span className="font-bold">{searchParams.get('discount')}</span> will be applied at checkout.
            </p>
          </div>
        )}

        {/* User Status Banner */}
        {isAuthenticated && (
          <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-green-800 font-medium">
                {paidSubscriptions.length > 0
                  ? `Welcome back, ${user?.firstName}! You have ${paidSubscriptions.length} active membership${paidSubscriptions.length !== 1 ? 's' : ''}: ${paidSubscriptions.map(sub => sub.package.name).join(', ')}.`
                  : `Welcome, ${user?.firstName}! Choose a plan to get started.`
                }
              </span>
            </div>
          </div>
        )}

        {/* Billing Toggle */}
        {sortedPackages.some(pkg => pkg.billingModel !== 'ONE_TIME') && (
          <div className="mb-12">
            <BillingToggle
              cycle={billingCycle}
              discount={sortedPackages.some(pkg => pkg.billingModel !== 'ONE_TIME' && pkg.priceMonthly < pkg.priceWeekly * 4.33)}
              onChange={(next: BillingCycle) => {
                if (next === billingCycle) return;
                setBillingCycle(next);
                try {
                  analyticsService.trackEvent('billing_cycle_change', {
                    pageType: 'pricing',
                    selectedDate: new Date().toISOString().split('T')[0],
                    metadata: {
                      newBillingCycle: next,
                      previousBillingCycle: billingCycle,
                      isAuthenticated,
                      packagesCount: sortedPackages.length,
                      ...(next === 'MONTHLY' ? { savings: getCycleSavings() } : {}),
                    }
                  });
                } catch (analyticsError) {
                  console.error('Analytics error:', analyticsError);
                }
              }}
            />
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(17rem,22rem))] md:justify-center gap-8">
          {sortedPackages.map((pkg) => {
            const price = getPriceForCycle(pkg);
            const isOneTime = pkg.billingModel === 'ONE_TIME';
            const features = [
              ...pkg.tagRules.map((rule) => rule?.tag?.name || 'Feature included'),
              ...(isOneTime && pkg.accessDurationDays ? [`${pkg.accessDurationDays} days access`] : []),
            ];

            return (
              <PlanCard
                key={pkg.id}
                name={pkg.name}
                price={price}
                period={isOneTime ? 'once' : billingCycle.toLowerCase()}
                description={pkg.description}
                features={features}
                popular={isPopularPlan(pkg.packageCode)}
              >
                {pkg.billingModel === 'ONE_TIME' && pkg.hasTrackRestrictions ? (
                  <div className="text-center">
                    <div className="w-full px-6 py-3 rounded-lg font-medium bg-gray-100 text-gray-600 mb-3">
                      Select Track First
                    </div>
                    <p className="text-sm text-gray-600">
                      {isAuthenticated ? (
                        <>
                          To purchase this package, please select a track from the{' '}
                          <Link to="/" className="text-racing-green hover:underline font-medium">
                            main page
                          </Link>{' '}
                          first, then proceed to checkout.
                        </>
                      ) : (
                        <>
                          To purchase this package, please{' '}
                          <Link to="/register" className="text-racing-green hover:underline font-medium">
                            sign up
                          </Link>{' '}
                          or{' '}
                          <Link to="/login" className="text-racing-green hover:underline font-medium">
                            login
                          </Link>{' '}
                          first, then select a track from the main page.
                        </>
                      )}
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => handleSubscribe(pkg)}
                    className={getButtonClasses(pkg)}
                    disabled={loading === pkg.id || isPackageActive(pkg)}
                  >
                    {loading === pkg.id ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      getButtonText(pkg)
                    )}
                  </button>
                )}
              </PlanCard>
            );
          })}
        </div>

        {/* CTA for non-authenticated users */}
        {!isAuthenticated && (
          <div className="mt-12 text-center">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Already have an account?
              </h3>
              <p className="text-blue-700 mb-4">
                Sign in to manage your memberships or continue with your current plan.
              </p>
              <Link
                to="/login"
                className="inline-block bg-deep-navy text-white px-6 py-3 rounded-lg font-medium hover:bg-deep-navy-dark transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-center text-deep-navy mb-8">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-deep-navy mb-2">Can I cancel anytime?</h3>
              <p className="text-gray-600 text-sm">
                Yes, you can cancel your membership anytime with 48 hours notice prior to expiry. Your access will continue until the end of your current billing period.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-deep-navy mb-2">Why pay monthly?</h3>
              <p className="text-gray-600 text-sm">
                Monthly membership runs for a full calendar month, not four weeks. Twelve monthly payments cover the whole year, where twelve four-week payments would only cover 48 weeks: that's 4 extra weeks of tips every year, and the monthly price is already discounted against weekly.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-deep-navy mb-2">What's included?</h3>
              <p className="text-gray-600 text-sm">
                Everything. One membership covers every race at every track we cover, with our tips, speed maps and results for each one.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-deep-navy mb-2">What payment methods do you accept?</h3>
              <p className="text-gray-600 text-sm">
                All transactions are processed securely through Stripe, accepting all major credit cards with bank-level security.
              </p>
            </div>
          </div>
        </div>

        {/* Promo code and Contact Support */}
        <div className="mt-12 text-center space-y-2">
          <p className="text-gray-600">
            Have a promo code?{' '}
            <button
              onClick={() => { setPromoInitialCode(undefined); setPromoModalOpen(true); }}
              className="text-racing-green hover:underline font-medium"
            >
              Redeem it here
            </button>
          </p>
          <p className="text-gray-600">
            Need help choosing the right plan?{' '}
            <Link to="/contact" className="text-racing-green hover:underline font-medium">
              Contact our support team
            </Link>
          </p>
        </div>
      </div>

      <PromoCodeModal
        isOpen={promoModalOpen}
        onClose={() => setPromoModalOpen(false)}
        initialCode={promoInitialCode}
      />
    </MainLayout>
  );
};

export default PricingPage;